/**
 * @file
 * RAOC-23: shared, in-tab OAuth-override store backing the "singleton" semantics
 * of {@link useRovoConfig}.
 *
 * ## Why this exists
 *
 * Multiple independent components may each call `useRovoConfig` (it is widely
 * shared and intentionally cheap to re-call). Each call is its own React hook
 * instance with its own `accessibleProductsConfig` state. When one instance's
 * `onOAuthSuccess` fires, it would only update *that* instance — so a connector
 * list rendered by a *different* instance would never reflect the new
 * `userNeedsOAuth: false` and would keep showing the connector as not connected.
 *
 * This module is a tiny global store of "products (optionally
 * datasources) that the user has just OAuth-connected, per cloudId". It is
 * written by `onOAuthSuccess` and **subscribed to by every `useRovoConfig`
 * instance** (via `useSyncExternalStore`). Instances overlay it onto their
 * derived `thirdPartyConfigs`, so a single OAuth success becomes reactively
 * visible across **all** instances — i.e. `useRovoConfig` behaves as a singleton
 * with respect to OAuth success actions.
 *
 * It is an **additive overlay**: it only ever forces `userNeedsOAuth` to
 * `false`. It never removes a connection and never fabricates a connector. On a
 * full reload the in-memory set is empty, but the persisted runtime cache
 * (`updateBaseCombinedConfigCacheEntry`) already re-derives the connected state,
 * so reload remains correct without this store. Or rather, persisting across reloads
 * is not the role of this store, we lean on the config/BE caching for that.
 *
 * Later improvements could layer a LS key set/subscribe here to persist into other tabs.
 */

const SENTINEL_VALUE = '\u0000product'; // \u0000 is unicode <NUL>

/**
 * Generate a unique key for a product (optionally a specific datasource) in a
 * given cloud. The sentinel is used for whole-product entries (no datasource).
 *
 * e.g.
 * - without datasource: 	abc-cloud<NUL>amplitude<NUL><NUL>product
 * - with datasource: 		abc-cloud<NUL>amplitude<NUL>cf41ec6c-d2a1-30fd-be10-3eb26e28d71c
 */
const makeKey = (cloudId: string, product: string, datasourceId?: string): string =>
	`${cloudId}\u0000${product}\u0000${datasourceId ?? SENTINEL_VALUE}`;

/** Set of `${cloudId}\0${product}\0${datasourceId|sentinel}` entries. */
const connected = new Set<string>();

/** Subscribers (one per mounted `useRovoConfig` instance). */
const listeners = new Set<() => void>();

/**
 * Monotonic version counter used as the `useSyncExternalStore` snapshot. A new
 * value is returned whenever the set changes, which is what triggers subscribed
 * components to re-render. (We expose a primitive version rather than the Set
 * itself so the snapshot is referentially stable between unrelated renders.)
 */
let version = 0;

/** `useSyncExternalStore` subscribe function. Stable identity. */
export const subscribeToOAuthOverrides = (onStoreChange: () => void): (() => void) => {
	listeners.add(onStoreChange);
	return () => {
		listeners.delete(onStoreChange);
	};
};

/** `useSyncExternalStore` snapshot getter. Stable identity. */
export const getOAuthOverridesVersion = (): number => version;

/**
 * Record that `product` (optionally a specific `datasourceId`) has been
 * OAuth-connected for `cloudId`. Notifies all subscribers. No-op (no version
 * bump, no notify) if the entry is already present, to avoid redundant renders.
 */
export const markOAuthConnected = (
	cloudId: string,
	product: string,
	datasourceId?: string,
): void => {
	const key = makeKey(cloudId, product, datasourceId);
	if (connected.has(key)) {
		return;
	}
	connected.add(key);
	version++;
	listeners.forEach((listener) => listener());
};

/**
 * Whether `product` (optionally `datasourceId`) has been recorded as
 * OAuth-connected for `cloudId` in this tab.
 */
export const isOAuthConnected = (
	cloudId: string,
	product: string,
	datasourceId?: string,
): boolean => connected.has(makeKey(cloudId, product, datasourceId));

/**
 * Whether `cloudId` has **any** recorded overrides. Cheap pre-check so the
 * overlay can be skipped entirely (returning the original config object) when
 * there is nothing to apply.
 */
export const hasAnyOAuthOverrideForCloud = (cloudId: string): boolean => {
	if (connected.size === 0) {
		return false;
	}
	const prefix = `${cloudId}\u0000`;
	for (const key of connected) {
		if (key.startsWith(prefix)) {
			return true;
		}
	}
	return false;
};

/** Test-only: clear all overrides and reset the version. */
export const __resetOAuthOverridesForTest = (): void => {
	connected.clear();
	listeners.clear();
	version = 0;
};
